import type { CustomAnalyticsChart } from "@redotech/redo-analytics-common/reports/custom-analytics-chart";
import { formatCustomAnalyticsReportTableValue } from "@redotech/redo-analytics-common/reports/custom-analytics-report-formatting";
import DownloadIcon from "@redotech/redo-icons/download-01.svg";
import FileIcon from "@redotech/redo-icons/file-02.svg";
import ImageIcon from "@redotech/redo-icons/image-01.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { CurrencyContext } from "@redotech/redo-web/currency";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useMutation } from "@tanstack/react-query";
import {
  memo,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { ChartBody } from "./chart-body";
import { MiniDetailSurface } from "./concierge-cards";
import {
  buildChartValueColumn,
  buildTruncationNote,
  exportChartAsPdf,
  exportChartAsPng,
} from "./custom-analytics-chart-card-utils";

type ChartDownloadFormat = "png" | "pdf";

const DOWNLOAD_MENU_ITEMS: RedoListItem<ChartDownloadFormat>[] = [
  {
    id: "png",
    type: "text",
    text: "Download as png",
    leadingItem: { type: "icon", Icon: ImageIcon },
    value: "png",
  },
  {
    id: "pdf",
    type: "text",
    text: "Download as PDF",
    leadingItem: { type: "icon", Icon: FileIcon },
    value: "pdf",
  },
];

export const CustomAnalyticsChartCard = memo(function CustomAnalyticsChartCard({
  chart,
}: {
  chart: CustomAnalyticsChart;
}) {
  const { formatCurrency } = useContext(CurrencyContext);
  const chartAreaRef = useRef<HTMLDivElement>(null);
  const [downloadMenuAnchor, setDownloadMenuAnchor] =
    useState<HTMLButtonElement | null>(null);
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);

  const valueColumn = useMemo(
    () =>
      buildChartValueColumn(
        chart.valueFormat,
        chart.series[0]?.label ?? "Value",
      ),
    [chart],
  );

  const formatValue = useCallback(
    (value: number) =>
      formatCustomAnalyticsReportTableValue({
        value,
        column: valueColumn,
        formatCurrency,
      }),
    [valueColumn, formatCurrency],
  );

  const truncationNote = buildTruncationNote(chart.truncation);

  const {
    mutate: handleDownload,
    isPending: exportPending,
    isError: exportFailed,
  } = useMutation({
    mutationFn: async (format: ChartDownloadFormat) => {
      const canvas = chartAreaRef.current?.querySelector("canvas");
      if (!canvas) {
        throw new Error("Chart canvas not found.");
      }
      if (format === "png") {
        await exportChartAsPng({ chart, canvas });
      } else {
        await exportChartAsPdf({ chart, canvas, formatValue, truncationNote });
      }
    },
  });

  return (
    <MiniDetailSurface
      badge={
        <Flex align="center" as="span" gap="none" shrink={0}>
          <RedoButton
            aria-expanded={downloadMenuOpen}
            aria-label="Download chart"
            hierarchy="tertiary"
            IconLeading={DownloadIcon}
            onClick={() => setDownloadMenuOpen((open) => !open)}
            pending={exportPending}
            ref={setDownloadMenuAnchor}
            size="xs"
          />
          <RedoListDropdown
            dropdownAnchor={downloadMenuAnchor}
            dropdownOpen={downloadMenuOpen}
            dropdownPlacement="bottom-end"
            items={DOWNLOAD_MENU_ITEMS}
            itemSelected={({ item }) => {
              if (item.type !== "text") {
                return;
              }
              setDownloadMenuOpen(false);
              handleDownload(item.value);
            }}
            setDropdownOpen={setDownloadMenuOpen}
            size="xs"
            uiSelector="ask-redo-chart-download"
          />
        </Flex>
      }
      subtitle={chart.dateRangeLabel ?? undefined}
      title={chart.title}
    >
      <Flex dir="column" ref={chartAreaRef} w="full">
        <ChartBody chart={chart} formatValue={formatValue} />
      </Flex>
      {truncationNote && (
        <Text fontSize="xxs" textColor="secondary">
          {truncationNote}
        </Text>
      )}
      {exportFailed && (
        <Text fontSize="xxs" textColor="error">
          The download could not be generated. Try again.
        </Text>
      )}
    </MiniDetailSurface>
  );
});
